import { DevicePluginFacade } from './facades';
import { initializingJWebAndStates } from './Initializer';
import { JWebCoreFacade } from './facades/JWebCoreFacade';
import { StaticDeviceInfoData } from './types';
import { ClientOSError } from '../../infra/errors/ClientOSError';
import { ClientOSErrorCodes } from '../../infra/errors/ErrorCodes';
export * from './facades';

// Re-export JWebEnums for convenience
export * as JWebEnums from './JWebEnums';

// Start initialization immediately on module load
initializingJWebAndStates().catch((error) => {
  // E.1: JWeb bootstrap failure can break global startup integrity, so propagate structured error.
  const normalizedError =
    error instanceof ClientOSError
      ? error
      : new ClientOSError(
          'Error during JWeb initialization',
          ClientOSErrorCodes.SERVICE_FAILED,
          error
        );
  console?.error?.('Error during JWeb initialization:', normalizedError);
  // Do not throw from this fire-and-forget module side-effect; propagation already
  // happens through rejected jWebStates promises consumed by critical paths.
});

// Backwards compatibility - delegate to JWebCoreFacade
export function JWebErrorHandler<T>(object: Record<string, any>): T {
  return JWebCoreFacade.JWebErrorHandler<T>(object);
}

// Backwards compatibility - delegate to JWebCoreFacade
export async function isNative(): Promise<boolean> {
  return await JWebCoreFacade.isNative();
}

// Backwards compatibility - delegate to JWebCoreFacade/DevicePluginFacade
export async function getPlatform(): Promise<string> {
  try {
    const platform = await JWebCoreFacade.getPlatformCached();
    if (platform) {
      return platform;
    }

    const isNativeEnvironment = await isNative();
    if (isNativeEnvironment) {
      return (await getStaticDeviceInfoData())?.platform || '';
    }

    return '';
  } catch (error) {
    console.error('Error in getPlatform:', error);
    return '';
  }
}

// Backwards compatibility - delegate to DevicePluginFacade
async function getStaticDeviceInfoData(): Promise<StaticDeviceInfoData> {
  const isNativeEnvironment = await isNative();
  try {
    if (isNativeEnvironment) {
      return (await DevicePluginFacade?.getDeviceInfoCached()) || {};
    }
  } catch (error) {
    console.error('Failed to get JWeb Device info:', error);
    return {};
  }
}

// Backwards compatibility - delegate to DevicePluginFacade
export async function getJWebAppVersion(): Promise<string> {
  try {
    const deviceInfo = await getStaticDeviceInfoData();
    return deviceInfo.appVersion || '';
  } catch (error) {
    console.error('Error in getJWebAppVersion:', error);
    return '';
  }
}

/**
 * Gets the full app version (appVersion + appBuild) from JWeb Device plugin
 * @returns Promise<string> - Full app version in format "version+build" or empty string on error
 */
export async function getJWebAppVersionFull(): Promise<string> {
  try {
    const deviceInfo = await getStaticDeviceInfoData();
    return deviceInfo.appVersionFull || '';
  } catch (error) {
    console.error('Error in getJWebAppVersionFull:', error);
    return '';
  }
}
